import React from 'react';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import Conditionals from '../Conditionals';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import './InteractiveSceneCharacters.css';
import CustomSelect from '../CustomSelect';

const INITIAL_DATA = {
  character_id: '',
  sprite_mix_id: '',
  start_sprite_id: '',
  conditionals: [],
  inactive: false
};

const InteractiveSceneCharacters = ({
  novelId,
  sceneId,
  scene,
  setScene,
  novel,
  loading,
  setLoading,
  error,
  setError,
  isProduction = false
}) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading);

  const handleEditCharacterClick = (character, index) => {
    openEdit({
      character_id: character.character_id,
      sprite_mix_id: character.sprite_mix_id,
      start_sprite_id: character.start_sprite_id,
      conditionals: character.conditionals || [],
      inactive: character.inactive || false
    }, index);
  };

  const handleSaveCharacter = async () => {
    if (!formData.character_id) {
      setError('Выберите персонажа');
      return;
    }
    setError(null);
    const updatedCharacters = [...(scene?.characters || [])];
    const newCharacter = {
      character_id: formData.character_id,
      sprite_mix_id: formData.sprite_mix_id || null,
      start_sprite_id: formData.start_sprite_id || null,
      conditionals: formData.conditionals,
      inactive: formData.inactive
    };
    if (editingIndex !== null) {
      updatedCharacters[editingIndex] = newCharacter;
    } else {
      updatedCharacters.push(newCharacter);
    }
    const success = await saveScene({ ...scene, characters: updatedCharacters }, 'Персонаж успешно сохранен!');
    if (success) close();
  };

  const handleDeleteCharacter = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого персонажа из сцены?')) return;
    const updatedCharacters = [...(scene?.characters || [])];
    updatedCharacters.splice(index, 1);
    await saveScene({ ...scene, characters: updatedCharacters }, 'Персонаж успешно удален!');
  };

  const getSelectedCharacter = () => novel?.characters?.find(char => char.id === formData.character_id) || null;
  const getSelectedSpriteMix = () => getSelectedCharacter()?.sprites?.find(sprite => sprite.id === formData.sprite_mix_id) || null;
  const getCharacterName = (characterId) => novel?.characters?.find(char => char.id === characterId)?.prompt_name || 'Неизвестный персонаж';
  const getSpriteMixName = (characterId, spriteMixId) => novel?.characters?.find(char => char.id === characterId)?.sprites?.find(sprite => sprite.id === spriteMixId)?.name || 'Неизвестный микс';
  const getSpriteAssetName = (spriteId) => novel?.assets?.sprites?.find(asset => asset.id === spriteId)?.name || 'Неизвестный спрайт';

  return (
    <div className="ischa-wrap">
      <div className="ischa-header">
        <h3 className="ischa-title">Персонажи сцены</h3>
      </div>

      <div className="ischa-add-wrap">
        <button className="ischa-add-btn" onClick={openAdd} disabled={loading}>
          <span className="button-icon">➕</span>
          Добавить персонажа
        </button>
      </div>

      {error && <div className="ischa-error">{error}</div>}

      {showForm && (
        <div className="ischa-overlay">
          <div className="ischa-modal">
            <h3 className="ischa-modal-title">{editingIndex !== null ? 'Редактировать персонажа' : 'Добавить персонажа'}</h3>

            <div className="ischa-form-group">
              <label className="ischa-label">Персонаж:</label>
              <CustomSelect
                value={formData.character_id}
                onChange={(e) => setFormData({ ...formData, character_id: e.target.value, sprite_mix_id: '', start_sprite_id: '' })}
                className="app-select" disabled={loading} required
              >
                <option value="">Выберите персонажа</option>
                {novel?.characters?.map((character) => (
                  <option key={character.id} value={character.id}>{character.prompt_name}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="ischa-form-group">
              <label className="ischa-label">Набор спрайтов:</label>
              <CustomSelect
                value={formData.sprite_mix_id}
                onChange={(e) => setFormData({ ...formData, sprite_mix_id: e.target.value, start_sprite_id: '' })}
                className="app-select" disabled={loading || !formData.character_id}
              >
                <option value="">Нет (Без спрайтов)</option>
                {getSelectedCharacter()?.sprites?.map((spriteMix) => (
                  <option key={spriteMix.id} value={spriteMix.id}>{spriteMix.name}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="ischa-form-group">
              <label className="ischa-label">Начальный спрайт:</label>
              <CustomSelect
                value={formData.start_sprite_id}
                onChange={(e) => setFormData({ ...formData, start_sprite_id: e.target.value })}
                className="app-select" disabled={loading || !formData.sprite_mix_id}
              >
                <option value="">Выберите начальный спрайт</option>
                {getSelectedSpriteMix()?.sprite_ids?.map((spriteId) => (
                  <option key={spriteId} value={spriteId}>{getSpriteAssetName(spriteId)}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="ischa-form-group">
              <label className="ischa-label">Условия:</label>
              <Conditionals
                flags={novel?.states?.flags || []}
                scales={novel?.states?.scales || []}
                endings={getAllEndingsFromNovel(novel)}
                value={formData.conditionals}
                onChange={(conditionals) => setFormData({ ...formData, conditionals })}
                disabled={loading}
              />
            </div>

            <div className="ischa-form-group">
              <div className="ischa-checkbox-wrap">
                <input
                  type="checkbox" className="ischa-checkbox"
                  checked={formData.inactive}
                  onChange={(e) => setFormData({ ...formData, inactive: e.target.checked })}
                  disabled={loading}
                />
                <label className="ischa-inactive-label">Неактивен (виден, но не может говорить)</label>
              </div>
            </div>

            <div className="ischa-form-actions">
              <button type="button" onClick={handleSaveCharacter} disabled={loading} className="ischa-btn-primary">
                {loading ? 'Загрузка...' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => { close(); setError(null); }} disabled={loading} className="ischa-btn-cancel">
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ischa-list">
        {scene?.characters?.length > 0 ? (
          scene.characters.map((character, index) => (
            <div key={index} className={`ischa-card ${character.inactive ? 'ischa-card--inactive' : ''}`}>
              <h4 className="ischa-card-name">
                {getCharacterName(character.character_id)}
                {character.inactive && <span className="ischa-inactive-badge">Неактивен</span>}
              </h4>
              <div className="ischa-card-actions">
                <button className="ischa-btn-edit" onClick={() => handleEditCharacterClick(character, index)} disabled={loading}>
                  Редактировать персонажа
                </button>
                <button className="ischa-btn-delete" onClick={() => handleDeleteCharacter(index)} disabled={loading}>
                  Удалить
                </button>
              </div>
              <div className="ischa-card-body">
                {character.sprite_mix_id ? (
                  <>
                    <p><strong>Набор спрайтов:</strong> {getSpriteMixName(character.character_id, character.sprite_mix_id)}</p>
                    {character.start_sprite_id && (
                      <p><strong>Начальный спрайт:</strong> {getSpriteAssetName(character.start_sprite_id)}</p>
                    )}
                  </>
                ) : (
                  <p><strong>Набор спрайтов:</strong> Нет (Без спрайтов)</p>
                )}
                {character.conditionals?.length > 0 && (
                  <p><strong>Условия:</strong> {character.conditionals.length}</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="ischa-empty">
            <p>В этой сцене пока нет персонажей.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveSceneCharacters;
